import { useState } from 'react';
import clsx from 'clsx';
import { IconType } from 'react-icons';
import {
    MdOutlineAlarm,
    MdOutlineBedtime,
    MdOutlineCalendarMonth,
    MdOutlineEdit,
    MdOutlineWbTwilight,
} from 'react-icons/md';

import ClockGlyph from '@srm/ui/clock-glyph';
import { useMenuPick } from '@srm/ui/hooks/useMenuPick';
import Menu from '@srm/ui/menu';
import { buildSnoozePresets, SnoozePresetKey } from '@utils/snoozePresets';

import './SnoozeButton.scss';

// Статичная иконка пресета. У «через 30 минут» и «через час» статичной иконки нет — там живой
// циферблат ClockGlyph со стрелками на целевое время (отсутствие ключа = рисуем циферблат).
const PRESET_ICON: Partial<Record<SnoozePresetKey, IconType>> = {
    evening: MdOutlineBedtime,
    tomorrow: MdOutlineWbTwilight,
    nextWeek: MdOutlineCalendarMonth,
};

const MENU_WIDTH = 264;

interface SnoozeButtonProps {
    /** Выбран пресет — создать напоминание на эту дату. */
    onPickPreset: (date: Date) => void;
    /** «Выбрать время…» — открыть полноценное окно выбора даты/времени. */
    onPickCustom: () => void;
    /** Подпись/заголовок кнопки-триггера. */
    title?: string;
    /** Класс кнопки-триггера — чтобы вписать в хост (тост, дропдаун, карточку). */
    buttonClassName?: string;
    /** Сторона выпадения меню относительно кнопки. */
    align?: 'left' | 'right';
    /** Открыто ли меню — хосту, чтобы удержать раскрытые действия и паузу авто-скрытия. */
    onOpenChange?: (opened: boolean) => void;
    /** Текст рядом с иконкой — подписанная кнопка (карточка уведомления). */
    label?: string;
    /** Размер иконки будильника, по умолчанию 16. */
    iconSize?: number;
}

type SnoozeMenuProps = Pick<SnoozeButtonProps, 'onPickPreset' | 'onPickCustom'>;

/** Пункты меню — своя вёрстка (иконка, подпись, время); контейнер `role="menu"` и клавиатура — у `Menu` пакета. */
const SnoozeMenu = ({ onPickPreset, onPickCustom }: SnoozeMenuProps) => {
    const pick = useMenuPick();
    // меню монтируется при каждом открытии — время пресетов считается заново
    const [presets] = useState(buildSnoozePresets);

    return (
        <>
            <div className="snz__head">
                <MdOutlineAlarm size={14} />
                Напомнить позже
            </div>
            {presets.map((p) => {
                const Icon = PRESET_ICON[p.key];
                return (
                    <button
                        key={p.key}
                        type="button"
                        role="menuitem"
                        className="snz__row"
                        onClick={pick(() => onPickPreset(p.date))}
                    >
                        <span className="snz__ico">
                            {Icon ? <Icon size={18} /> : <ClockGlyph date={p.date} size={18} />}
                        </span>
                        <span className="snz__label">{p.label}</span>
                        <span className="snz__when">{p.when}</span>
                    </button>
                );
            })}
            <div className="snz__div" role="separator" />
            <button type="button" role="menuitem" className="snz__row snz__row--custom" onClick={pick(onPickCustom)}>
                <span className="snz__ico">
                    <MdOutlineEdit size={18} />
                </span>
                <span className="snz__label">Выбрать время…</span>
            </button>
        </>
    );
};

/**
 * Кнопка «Отложить» с меню быстрых пресетов (30 минут, час, вечером, завтра, через неделю) и пунктом «Выбрать
 * время…» — `Menu` пакета со своей вёрсткой пунктов. Меню в портале — не обрезается скролл-контейнерами: кнопка
 * живёт в превью-тосте, дропдауне уведомлений и на карточке уведомления.
 */
const SnoozeButton = ({
    onPickPreset,
    onPickCustom,
    title = 'Отложить',
    buttonClassName,
    align = 'right',
    onOpenChange,
    label,
    iconSize = 16,
}: SnoozeButtonProps) => {
    const [opened, setOpened] = useState(false);

    // щелчки кнопки и пунктов (портал всплывает по дереву React) не доходят до хоста — строки-ссылки и т. п.
    return (
        <div className="snz" onClick={(e) => e.stopPropagation()}>
            <Menu
                content={<SnoozeMenu onPickPreset={onPickPreset} onPickCustom={onPickCustom} />}
                contentRole="menu"
                opened={opened}
                onOpenChange={(next) => {
                    setOpened(next);
                    onOpenChange?.(next);
                }}
                position={align === 'right' ? 'bottom-end' : 'bottom-start'}
                width={MENU_WIDTH}
                popoverProps={{
                    className: clsx('snz__menu', `snz__menu--${align}`),
                    listClassName: 'snz__list',
                    verticalOffset: 8,
                }}
            >
                <button
                    type="button"
                    className={clsx(
                        'snz__trigger',
                        label && 'snz__trigger--labeled',
                        buttonClassName,
                        opened && 'snz__trigger--open',
                    )}
                    title={title}
                    aria-label={title}
                    // хост может быть ссылкой (строка уведомления) — щелчок по кнопке не переходит по ней
                    onClick={(e) => e.preventDefault()}
                >
                    <MdOutlineAlarm size={iconSize} />
                    {label && <span className="snz__trigger-text">{label}</span>}
                </button>
            </Menu>
        </div>
    );
};

export default SnoozeButton;
